import { useQuery, useMutation } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { Tables } from "@/integrations/supabase/types"
import { useToast } from "@/hooks/use-toast"
import { queryClient } from "@/lib/react-query"

type MeetingRoom = Tables<"meeting_rooms">

export const useMeetings = () => {
  const { profile } = useAuth()
  const { toast } = useToast()

  const query = useQuery({
    queryKey: ["meetings", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("meeting_rooms")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .order("scheduled_date", { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!profile?.condominium_id
  })

  const createMeeting = useMutation({
    mutationFn: async (meetingData: {
      title: string
      description?: string
      scheduled_date: string
      duration_minutes?: number
      max_participants?: number
      meeting_password?: string
      is_recorded?: boolean
      status?: 'scheduled' | 'live' | 'ended'
    }) => {
      if (!profile?.id || !profile?.condominium_id) throw new Error("Usuário não autenticado")

      const { data: roomId, error: roomError } = await supabase.rpc("generate_jitsi_room_id")
      if (roomError) throw roomError
      
      const { data, error } = await supabase
        .from("meeting_rooms")
        .insert({
          title: meetingData.title,
          description: meetingData.description,
          scheduled_date: meetingData.scheduled_date,
          duration_minutes: meetingData.duration_minutes ?? 60,
          max_participants: meetingData.max_participants ?? 50,
          meeting_password: meetingData.meeting_password,
          is_recorded: meetingData.is_recorded ?? false,
          status: meetingData.status ?? 'scheduled',
          created_by: profile.id,
          condominium_id: profile.condominium_id,
          jitsi_room_id: roomId as string
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      toast({
        title: "Reunião criada",
        description: "A reunião foi criada com sucesso!"
      })
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar reunião",
        description: (error as any).message,
        variant: "destructive"
      })
    }
  })

  const updateMeeting = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<MeetingRoom> & { id: string }) => {
      const { data, error } = await supabase
        .from("meeting_rooms")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      toast({
        title: "Reunião atualizada",
        description: "A reunião foi atualizada com sucesso!"
      })
    }
  })

  const deleteMeeting = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("meeting_rooms")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      toast({
        title: "Reunião excluída",
        description: "A reunião foi excluída com sucesso!"
      })
    }
  })

  return {
    meetings: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    createMeeting: createMeeting.mutate,
    updateMeeting: updateMeeting.mutate,
    deleteMeeting: deleteMeeting.mutate,
    isCreating: createMeeting.isPending,
    isUpdating: updateMeeting.isPending,
    isDeleting: deleteMeeting.isPending
  }
}

export const useMeeting = (id: string) => {
  return useQuery({
    queryKey: ["meeting", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("meeting_rooms")
        .select("*")
        .eq("id", id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!id
  })
}